import React from 'react';
import { Plus, Trash2, Users, DollarSign } from 'lucide-react';

const PriceTierManager = ({ basePrice, priceTiers, setPriceTiers }) => {
  const addTier = () => {
    const newTier = {
      groupSize: priceTiers.length > 0 ? priceTiers[priceTiers.length - 1].groupSize + 10 : 10,
      price: basePrice ? basePrice * 0.9 : 0,
      discount: 10
    };
    setPriceTiers([...priceTiers, newTier]);
  };

  const removeTier = (index) => {
    setPriceTiers(priceTiers.filter((_, i) => i !== index));
  };

  const updateTier = (index, field, value) => {
    const newTiers = [...priceTiers];
    newTiers[index][field] = parseFloat(value) || 0;
    
    // Calculate discount percentage
    if (field === 'price' && basePrice) {
      newTiers[index].discount = Math.round(((basePrice - newTiers[index].price) / basePrice) * 100);
    }
    
    setPriceTiers(newTiers);
  };

  const calculateDiscount = (tierPrice) => {
    if (!basePrice || basePrice === 0) return 0;
    return Math.round(((basePrice - tierPrice) / basePrice) * 100);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Price Tiers</h3>
          <p className="text-sm text-gray-600">Offer discounts for larger group orders</p>
        </div>
        <button
          type="button"
          onClick={addTier}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={18} />
          <span>Add Tier</span>
        </button>
      </div>

      {/* Base Price Display */}
      {basePrice && (
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <DollarSign size={20} className="text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Base Price:</span>
            </div>
            <span className="text-lg font-bold text-gray-900">
              ${parseFloat(basePrice).toFixed(2)}
            </span>
          </div>
        </div>
      )}

      {/* Tiers List */}
      {priceTiers.length === 0 ? (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
          <Users className="mx-auto mb-2 text-blue-600" size={32} />
          <p className="text-sm font-medium text-blue-900">No price tiers yet</p>
          <p className="text-xs text-blue-700 mt-1">
            Add tiers to offer discounts for larger groups
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {priceTiers
            .sort((a, b) => a.groupSize - b.groupSize)
            .map((tier, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
              >
                <div className="flex items-center space-x-4">
                  {/* Group Size */}
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      <Users size={14} className="inline mr-1" />
                      Group Size
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={tier.groupSize}
                      onChange={(e) => updateTier(index, 'groupSize', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="10"
                    />
                  </div>

                  {/* Price */}
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      <DollarSign size={14} className="inline mr-1" />
                      Price per Unit
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={tier.price}
                      onChange={(e) => updateTier(index, 'price', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0.00"
                    />
                  </div>

                  {/* Discount Display */}
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Discount
                    </label>
                    <div className="px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-center">
                      <span className="text-lg font-bold text-green-700">
                        {calculateDiscount(tier.price)}%
                      </span>
                    </div>
                  </div>

                  {/* Delete Button */}
                  <div className="pt-6">
                    <button
                      type="button"
                      onClick={() => removeTier(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove tier"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>

                {/* Savings Preview */}
                {basePrice && tier.price < basePrice && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-600">
                      Members save{' '}
                      <span className="font-bold text-green-600">
                        ${(basePrice - tier.price).toFixed(2)}
                      </span>{' '}
                      per unit when {tier.groupSize}+ people join
                    </p>
                  </div>
                )}
              </div>
            ))}
        </div>
      )}

      {/* Pricing Strategy Tips */}
      {priceTiers.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-blue-900 mb-2">💡 Pricing Tips:</h4>
          <ul className="text-xs text-blue-800 space-y-1">
            <li>• Typical discounts range from 10-30% for group orders</li>
            <li>• Higher discounts attract more buyers to join groups</li>
            <li>• Create multiple tiers to incentivize larger groups</li>
            <li>• Consider your margins when setting bulk prices</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default PriceTierManager;